import { StateCreator } from 'zustand';
import { ID } from '@rossum/api-client';
import { SupportedAnnotationView } from '../../document-list-base/supportedAnnotationViews';
import {
  AUTO_DETECT,
  TranslateLanguage,
  TranslateSourceLanguage,
} from '../document-translation/languages';

type DocumentSidebarState = {
  messagesVisible: boolean;
  blockersVisible: boolean;
  topbarVisible: boolean;
  annotationDataVisible: boolean;
};

type TranslationState = {
  targetLanguage: TranslateLanguage | undefined;
  sourceLanguage: TranslateSourceLanguage;
  searchValue: string;
};

export type DocumentInfoState = {
  currentDrawer: SupportedAnnotationView | undefined;
  annotationId: ID | undefined;
  relationId: ID | undefined;
  sidebarState: DocumentSidebarState;
  translationState: TranslationState;
};

type DocumentTranslationActions = {
  setTranslationState: (state: Partial<TranslationState>) => void;
};

type DocumentSidebarActions = {
  setMessagesVisibility: (visible: boolean) => void;
  setBlockersVisibility: (visible: boolean) => void;
  toggleBlockers: () => void;
  setTopbarVisibility: (visible: boolean) => void;
  toggleAnnotationDataVisible: () => void;
};

export type DocumentInfoActions = {
  closeDrawer: () => void;
  openDrawer: (params: {
    drawer: SupportedAnnotationView;
    annotationId: ID;
    relationId?: ID;
  }) => void;
  sidebarActions: DocumentSidebarActions;
  translationActions: DocumentTranslationActions;
};

export type DocumentInfoStoreType = DocumentInfoState & DocumentInfoActions;

export const documentInfoStoreSlice: StateCreator<
  DocumentInfoStoreType,
  [],
  [],
  DocumentInfoStoreType
> = set => ({
  currentDrawer: undefined,
  annotationId: undefined,
  relationId: undefined,
  closeDrawer: () => {
    set({
      currentDrawer: undefined,
      annotationId: undefined,
      relationId: undefined,
    });
  },
  openDrawer: ({ drawer, annotationId, relationId }) => {
    set({
      currentDrawer: drawer,
      annotationId,
      relationId,
    });
  },
  sidebarState: {
    messagesVisible: false,
    blockersVisible: false,
    topbarVisible: true,
    annotationDataVisible: false,
  },
  sidebarActions: {
    toggleAnnotationDataVisible: () => {
      set(state => ({
        ...state,
        sidebarState: {
          ...state.sidebarState,
          annotationDataVisible: !state.sidebarState.annotationDataVisible,
        },
      }));
    },
    setMessagesVisibility: (visible: boolean) => {
      set(state => ({
        ...state,
        sidebarState: {
          ...state.sidebarState,
          messagesVisible: visible,
        },
      }));
    },
    setBlockersVisibility: (visible: boolean) => {
      set(state => ({
        ...state,
        sidebarState: {
          ...state.sidebarState,
          blockersVisible: visible,
        },
      }));
    },
    toggleBlockers: () => {
      set(state => ({
        ...state,
        sidebarState: {
          ...state.sidebarState,
          blockersVisible: !state.sidebarState.blockersVisible,
        },
      }));
    },
    setTopbarVisibility: visible => {
      set(state => ({
        ...state,
        sidebarState: {
          ...state.sidebarState,
          topbarVisible: visible,
        },
      }));
    },
  },
  translationActions: {
    setTranslationState: config =>
      set(state => ({
        translationState: { ...state.translationState, ...config },
      })),
  },
  translationState: {
    targetLanguage: undefined,
    sourceLanguage: AUTO_DETECT,
    searchValue: '',
  },
});
